import type { QueryClient } from "@tanstack/react-query";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import { fetchMediaAsset } from "@/lib/utils/media/fetch-media-asset";
import { getMediaAssetQueryKey } from "@/lib/utils/media/media-asset-query-key";
import {
  PUBLIC_PRESIGNED_URL_GC_MS,
  PUBLIC_PRESIGNED_URL_STALE_MS,
} from "@/lib/utils/media/public-media-cache";

export async function prefetchTeacherProfileMedia(
  queryClient: QueryClient,
  tenantId: number,
  teacherId: number,
) {
  if (tenantId <= 0 || teacherId <= 0) return queryClient;

  await queryClient.prefetchQuery({
    queryKey: getMediaAssetQueryKey(
      tenantId,
      EntryType.Teacher,
      teacherId,
      MediaGroup.Profile,
      "public",
    ),
    queryFn: () =>
      fetchMediaAsset({
        tenantId,
        ownerType: "Teacher",
        ownerId: teacherId,
        mediaGroup: MediaGroup.Profile,
      }).catch(() => ({
        url: null,
        mediaId: null,
        media: null,
      })),
    staleTime: PUBLIC_PRESIGNED_URL_STALE_MS,
    gcTime: PUBLIC_PRESIGNED_URL_GC_MS,
  });

  return queryClient;
}

/** Prefetch profile images for a teachers listing (avoids per-card client N+1). */
export async function prefetchTeacherProfileMediaMany(
  queryClient: QueryClient,
  tenantId: number,
  teacherIds: ReadonlyArray<unknown>,
) {
  if (tenantId <= 0) return queryClient;

  const uniqueIds = [
    ...new Set(
      teacherIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    ),
  ];

  await Promise.all(
    uniqueIds.map((teacherId) =>
      prefetchTeacherProfileMedia(queryClient, tenantId, teacherId),
    ),
  );

  return queryClient;
}
